// eslint-disable-next-line no-restricted-imports
import {useChatState} from '@github-ui/copilot-chat/CopilotChatContext'
import {copilotFeatureFlags} from '@github-ui/copilot-feature-flags'
import type {CopilotSpaceResource} from '../../types'
import {useImperativeHandle, useMemo, useRef} from 'react'

import {useUploadedFileProcessing} from './hooks/use-uploaded-file-processing'
import {UploadableFileExtensions} from './utils/uploadable-file-extensions'

interface FileUploadInputProps {
  onResourcesAdded: (resources: CopilotSpaceResource[]) => void
  onError?: (errorMessages: string[]) => void
  onUploadingChange?: (uploading: boolean) => void
  ref?: React.Ref<FileUploadInputRef>
}

export interface FileUploadInputRef {
  addResources: () => void
}

export const FileUploadInput = ({ref, onResourcesAdded, onError, onUploadingChange}: FileUploadInputProps) => {
  const {processFiles} = useUploadedFileProcessing({
    onResourcesAdded,
    onError,
    onUploadingChange,
  })

  const {availableModels} = useChatState()
  const acceptAttribute = useMemo(() => {
    const textExtensions = Array.from(UploadableFileExtensions.text)
    const imageExtensions = copilotFeatureFlags.spacesImagesEnabled
      ? Array.from(UploadableFileExtensions.images(availableModels))
      : []
    const allExtensions = [...textExtensions, ...imageExtensions]
    // Accept text/* MIME types and specific file extensions
    return ['text/*', ...allExtensions].join(',')
  }, [availableModels])

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      void processFiles(e.target.files, {source: 'file-upload'})
      e.target.value = '' // Reset the input value to allow the same file to be selected again
    }
  }

  const fileInputRef = useRef<HTMLInputElement>(null)
  useImperativeHandle(ref, () => ({
    addResources: () => fileInputRef.current?.click(),
  }))

  return (
    <input
      data-testid="file-upload-input"
      ref={fileInputRef}
      type="file"
      accept={acceptAttribute}
      multiple
      style={{display: 'none'}}
      onChange={onChange}
    />
  )
}

FileUploadInput.displayName = 'FileUploadInput'
